// Backlog 016 — `grid domains`: self-serve custom domains.
//
//   grid domains add <fqdn> --app <app>       register a host domain for one app
//   grid domains add <apex> --scope grid      attach a whole zone to the grid (Decision 055)
//   grid domains list                         status table with the exact record + next step
//   grid domains verify <fqdn>                check DNS now instead of waiting for the sweeper
//   grid domains remove <fqdn>                remove the domain from the registry
//
// Two scopes (Decision 055). `host` (default) binds one FQDN to one app —
// the flow: add → set the printed DNS record at your provider → verify → add
// the domain to custom_domains in cloudgrid.yaml → plug. `grid` attaches a
// registrable apex zone (acme.com) to the whole grid, verified once; every app
// then serves at <app>.acme.com with no per-label verification. §23 voice
// throughout.
import { Command } from 'commander';
import { getV2Client, type IV2Client } from '../api/v2-client.js';
import { getActiveOrg } from '../active-org.js';
import { handleV2Error } from '../handle-error.js';
import { Output } from '../output.js';
import { addGridOption, gridSlug, type GridOpts } from '../grid-opt.js';

interface DomainsOpts extends GridOpts {
  json?: boolean;
  app?: string;
  scope?: string;
}

/**
 * A registrable apex is a bare two-label domain like `acme.com`. A grid zone
 * must be an apex — apps serve at `<app>.acme.com` — so a subdomain label is
 * rejected at add time. Mirrors the server's `isRegistrableApex` two-label
 * heuristic (packages/api/src/services/custom-domains.ts); the server stays the
 * source of truth, this is only an early, friendlier message.
 */
function looksLikeRegistrableApex(domain: string): boolean {
  return domain.split('.').length === 2;
}

/** Wire shapes (mirror routes/v2/domains.ts DomainResponse). */
interface DnsRecordInstruction {
  type: 'CNAME' | 'ALIAS' | 'TXT';
  name: string;
  value: string;
  note?: string;
}
interface DomainResponse {
  domain: {
    domain_id: string;
    domain: string;
    scope: 'grid' | 'host';
    verification_status: 'pending' | 'verified' | 'failed';
    cert_status: string;
    verified_at: string | null;
    last_checked_at?: string;
  };
  dns_records: DnsRecordInstruction[];
  hint: string;
  app: string | null;
}

/** Resolve client + the grid segment for `/api/v2/grids/<grid>/domains` paths. */
async function resolveCtx(
  opts: GridOpts,
  client?: IV2Client,
  gridRef?: string,
): Promise<{ c: IV2Client; grid: string }> {
  const override = gridSlug(opts);
  const c = client ?? (await getV2Client(override));
  if (gridRef) return { c, grid: gridRef };
  const active = await getActiveOrg(c, override !== undefined ? { override } : {});
  return { c, grid: active.slug };
}

function renderRecords(records: DnsRecordInstruction[]): string[] {
  const lines = [records.length === 1
    ? 'Add this record at your DNS provider:'
    : 'Add these records at your DNS provider:'];
  for (const r of records) {
    const note = r.note ? `   (${r.note})` : '';
    lines.push(`  ${r.type.padEnd(5)} ${r.name} → ${r.value}${note}`);
  }
  return lines;
}

export async function runDomainsAdd(
  fqdn: string,
  opts: DomainsOpts,
  client?: IV2Client,
  gridRef?: string,
): Promise<void> {
  const out = Output.fromOpts({ json: !!opts.json, quiet: false });

  // Scope. `host` (default) binds one FQDN to one app; `grid` attaches a whole
  // apex zone to the grid so every app can serve at <app>.<zone> once the zone
  // verifies (Decision 055). The server is the source of truth; these checks
  // just fail fast with a friendlier message before any network call.
  const scope = opts.scope ?? 'host';
  if (scope !== 'host' && scope !== 'grid') {
    throw new Error(`Unknown scope '${scope}'. Use 'host' for one app or 'grid' for a whole zone.`);
  }

  const domain = fqdn.trim().toLowerCase();
  if (scope === 'grid') {
    if (!looksLikeRegistrableApex(domain)) {
      throw new Error(
        `A grid zone must be a registrable apex like acme.com, not '${domain}'. ` +
        `To serve one app at this exact name, use host scope: grid domains add ${domain} --app <app>.`,
      );
    }
  } else if (!opts.app) {
    throw new Error(
      `Name the app this domain serves: grid domains add ${domain} --app <app>. ` +
      `To attach a whole zone instead, use: grid domains add <apex> --scope grid.`,
    );
  }

  const { c, grid } = await resolveCtx(opts, client, gridRef);
  const body = scope === 'grid'
    ? { domain, scope: 'grid' as const }
    : { domain, app: opts.app };
  const res = await c.post<DomainResponse>(`/api/v2/grids/${grid}/domains`, body);
  out.json(() => res);
  out.human(() => {
    if (res.domain.scope === 'grid') {
      const lines = [
        `Added ${res.domain.domain} as a grid zone (pending DNS). ` +
          `Once verified, any app in this grid can serve at <app>.${res.domain.domain}.`,
        '',
        ...renderRecords(res.dns_records),
        '',
        `Set these two records, then run 'grid domains verify ${res.domain.domain}'. ` +
          `CloudGrid also checks every few minutes.`,
      ];
      return lines.join('\n');
    }
    const lines = [
      `Added ${res.domain.domain} → ${res.app ?? opts.app} (pending DNS).`,
      '',
      ...renderRecords(res.dns_records),
      '',
      `Then run 'grid domains verify ${res.domain.domain}'. CloudGrid also checks every few minutes.`,
    ];
    return lines.join('\n');
  });
}

export async function runDomainsList(
  opts: DomainsOpts,
  client?: IV2Client,
  gridRef?: string,
): Promise<void> {
  const out = Output.fromOpts({ json: !!opts.json, quiet: false });
  const { c, grid } = await resolveCtx(opts, client, gridRef);
  const { domains } = await c.get<{ domains: DomainResponse[] }>(`/api/v2/grids/${grid}/domains`);
  out.json(() => domains);
  out.human(() => {
    if (domains.length === 0) {
      return "No custom domains. Add one with 'grid domains add <fqdn> --app <app>'.";
    }
    const lines: string[] = ['Custom domains:'];
    for (const d of domains) {
      const app = d.app ? ` → ${d.app}` : '';
      const cert = d.domain.cert_status === 'none' ? '' : `, cert ${d.domain.cert_status}`;
      lines.push(`  ${d.domain.domain}${app} — ${d.domain.verification_status}${cert}`);
      lines.push(`    ${d.hint}`);
    }
    return lines.join('\n');
  });
}

/** Find a registry row by FQDN (verify/remove address rows by domain, not id). */
async function findByFqdn(c: IV2Client, grid: string, fqdn: string): Promise<DomainResponse> {
  const { domains } = await c.get<{ domains: DomainResponse[] }>(`/api/v2/grids/${grid}/domains`);
  const match = domains.find((d) => d.domain.domain === fqdn.trim().toLowerCase());
  if (!match) {
    throw new Error(
      `'${fqdn}' is not in this grid's domain registry. See 'grid domains list'.`,
    );
  }
  return match;
}

export async function runDomainsVerify(
  fqdn: string,
  opts: DomainsOpts,
  client?: IV2Client,
  gridRef?: string,
): Promise<void> {
  const out = Output.fromOpts({ json: !!opts.json, quiet: false });
  const { c, grid } = await resolveCtx(opts, client, gridRef);
  const row = await findByFqdn(c, grid, fqdn);
  const res = await c.post<DomainResponse>(
    `/api/v2/grids/${grid}/domains/${row.domain.domain_id}/verify`,
  );
  out.json(() => res);
  out.human(() => {
    const status = res.domain.verification_status;
    if (status === 'verified') {
      return `${res.domain.domain} is verified.\n${res.hint}`;
    }
    if (status === 'failed') {
      return `${res.domain.domain} can't be verified.\n${res.hint}`;
    }
    return `${res.domain.domain} is still pending.\n${res.hint}`;
  });
}

export async function runDomainsRemove(
  fqdn: string,
  opts: DomainsOpts,
  client?: IV2Client,
  gridRef?: string,
): Promise<void> {
  const out = Output.fromOpts({ json: !!opts.json, quiet: false });
  const { c, grid } = await resolveCtx(opts, client, gridRef);
  const row = await findByFqdn(c, grid, fqdn);
  const res = await c.del<{ removed: boolean; domain: string }>(
    `/api/v2/grids/${grid}/domains/${row.domain.domain_id}`,
  );
  out.json(() => res);
  out.human(() => `Removed ${res.domain}. Also remove it from custom_domains in cloudgrid.yaml if it's still there.`);
}

export const domainsCommand = new Command('domains').description(
  'Serve apps on your own domain',
);

const addCmd = domainsCommand
  .command('add <fqdn>')
  .description('Register a custom domain for one app, or a whole zone for the grid')
  .option('--app <name>', 'The app this domain serves (required for host scope)')
  .option('--scope <scope>', "'host' (default) for one app, or 'grid' for a whole apex zone", 'host')
  .option('--json', 'JSON output');
addGridOption(addCmd, 'Override active grid');
addCmd.action(async (fqdn: string, opts: DomainsOpts) => {
  try { await runDomainsAdd(fqdn, opts); }
  catch (err) { await handleV2Error(err, { json: !!opts.json }); }
});

const listCmd = domainsCommand
  .command('list')
  .description('List custom domains with status and next step')
  .option('--json', 'JSON output');
addGridOption(listCmd, 'Override active grid');
listCmd.action(async (opts: DomainsOpts) => {
  try { await runDomainsList(opts); }
  catch (err) { await handleV2Error(err, { json: !!opts.json }); }
});

const verifyCmd = domainsCommand
  .command('verify <fqdn>')
  .description('Check the DNS records now')
  .option('--json', 'JSON output');
addGridOption(verifyCmd, 'Override active grid');
verifyCmd.action(async (fqdn: string, opts: DomainsOpts) => {
  try { await runDomainsVerify(fqdn, opts); }
  catch (err) { await handleV2Error(err, { json: !!opts.json }); }
});

const removeCmd = domainsCommand
  .command('remove <fqdn>')
  .description('Remove a custom domain from the registry')
  .option('--json', 'JSON output');
addGridOption(removeCmd, 'Override active grid');
removeCmd.action(async (fqdn: string, opts: DomainsOpts) => {
  try { await runDomainsRemove(fqdn, opts); }
  catch (err) { await handleV2Error(err, { json: !!opts.json }); }
});
